<template>
  <div class="tw-max-w-2xl tw-mx-auto">
    <!-- Pending / awaiting confirmation -->
    <div
      v-if="pending"
      class="tw-flex tw-justify-center tw-items-center tw-min-h-[320px]"
    >
      <div class="tw-bg-white tw-rounded-xl tw-shadow-md tw-px-8 tw-py-10 tw-max-w-md tw-w-full tw-text-center">
        <div class="tw-flex tw-justify-center tw-mb-4">
          <span class="tw-inline-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-full tw-bg-amber-50">
            <span class="tw-h-8 tw-w-8 tw-border-2 tw-border-amber-500 tw-border-t-transparent tw-rounded-full tw-animate-spin" />
          </span>
        </div>
        <h2 class="tw-text-lg tw-font-semibold tw-text-slate-900">
          Awaiting confirmation
        </h2>
        <p class="tw-mt-2 tw-text-sm tw-text-slate-500">
          Your order is being processed. This page will update automatically once the order has been confirmed.
        </p>
        <p class="tw-mt-4 tw-text-xs tw-text-slate-400">
          Checking every few seconds&hellip;
        </p>
      </div>
    </div>

    <!-- Confirmed order details -->
    <div
      v-else
      class="tw-space-y-6"
    >
      <!-- Success banner -->
      <div class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-6 tw-border tw-border-emerald-200">
        <div class="tw-flex tw-items-start tw-gap-3">
          <div class="tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-50 tw-shrink-0">
            <svg
              class="tw-h-5 tw-w-5 tw-text-emerald-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <div>
            <h2 class="tw-text-lg tw-font-semibold tw-text-slate-900">
              Order confirmed
            </h2>
            <p class="tw-mt-1 tw-text-sm tw-text-slate-600">
              Thank you — your order has been confirmed and is now being processed.
            </p>
          </div>
        </div>
      </div>

      <!-- Order details card -->
      <div class="tw-bg-white tw-rounded-xl tw-shadow-md tw-p-6 tw-border tw-border-slate-100">
        <h3 class="tw-text-base tw-font-semibold tw-text-slate-900 tw-mb-4">
          Order details
        </h3>

        <dl class="tw-divide-y tw-divide-slate-100">
          <div
            v-if="order.customer_name"
            class="tw-py-3 tw-flex tw-justify-between tw-text-sm"
          >
            <dt class="tw-text-slate-500">
              Customer
            </dt>
            <dd class="tw-text-slate-900 tw-font-medium">
              {{ order.customer_name }}
            </dd>
          </div>

          <div
            v-if="order.customer_email"
            class="tw-py-3 tw-flex tw-justify-between tw-text-sm"
          >
            <dt class="tw-text-slate-500">
              Email
            </dt>
            <dd class="tw-text-slate-900 tw-font-medium">
              {{ order.customer_email }}
            </dd>
          </div>

          <div
            v-if="order.customer_phone"
            class="tw-py-3 tw-flex tw-justify-between tw-text-sm"
          >
            <dt class="tw-text-slate-500">
              Phone
            </dt>
            <dd class="tw-text-slate-900 tw-font-medium">
              {{ order.customer_phone }}
            </dd>
          </div>

          <div
            v-if="order.po_number"
            class="tw-py-3 tw-flex tw-justify-between tw-text-sm"
          >
            <dt class="tw-text-slate-500">
              PO number
            </dt>
            <dd class="tw-text-slate-900 tw-font-medium">
              {{ order.po_number }}
            </dd>
          </div>

          <div
            v-if="order.customer_reference"
            class="tw-py-3 tw-flex tw-justify-between tw-text-sm"
          >
            <dt class="tw-text-slate-500">
              Customer reference
            </dt>
            <dd class="tw-text-slate-900 tw-font-medium">
              {{ order.customer_reference }}
            </dd>
          </div>

          <div
            v-if="order.approver_name"
            class="tw-py-3 tw-flex tw-justify-between tw-text-sm"
          >
            <dt class="tw-text-slate-500">
              Approver
            </dt>
            <dd class="tw-text-slate-900 tw-font-medium">
              {{ order.approver_name }}
              <span
                v-if="order.approver_email"
                class="tw-text-slate-500 tw-font-normal"
              >({{ order.approver_email }})</span>
            </dd>
          </div>

          <div
            v-if="order.preferred_delivery_week"
            class="tw-py-3 tw-flex tw-justify-between tw-text-sm"
          >
            <dt class="tw-text-slate-500">
              Preferred delivery week
            </dt>
            <dd class="tw-text-slate-900 tw-font-medium">
              {{ order.preferred_delivery_week }}
            </dd>
          </div>

          <div
            v-if="order.on_account"
            class="tw-py-3 tw-flex tw-justify-between tw-text-sm"
          >
            <dt class="tw-text-slate-500">
              Payment method
            </dt>
            <dd class="tw-text-slate-900 tw-font-medium">
              On account
            </dd>
          </div>

          <div class="tw-py-3 tw-flex tw-justify-between tw-text-sm">
            <dt class="tw-text-slate-500 tw-font-semibold">
              Total
            </dt>
            <dd class="tw-text-slate-900 tw-font-bold tw-text-base">
              £{{ formattedTotal }}
            </dd>
          </div>
        </dl>

        <div
          v-if="order.notes"
          class="tw-mt-4 tw-pt-4 tw-border-t tw-border-slate-100"
        >
          <h4 class="tw-text-sm tw-font-medium tw-text-slate-500 tw-mb-1">
            Notes
          </h4>
          <p class="tw-text-sm tw-text-slate-700 tw-whitespace-pre-line">
            {{ order.notes }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'

export default {
  name: 'OrderConfirmation',
  props: {
    order: {
      type: Object,
      required: true
    },
    pending: {
      type: Boolean,
      default: false
    }
  },
  setup(props) {
    const formattedTotal = computed(() => {
      const val = parseFloat(props.order.total)
      return isNaN(val) ? '0.00' : val.toFixed(2)
    })

    return {
      formattedTotal
    }
  }
}
</script>
